import {
    REFERRAL_ATTR_ITEM_ID,
    REFERRAL_ATTR_PROFESSIONAL_ID,
    REFERRAL_ATTR_PROFESSIONAL_TITLE,
    REFERRAL_ATTR_SOURCE,
    TRACK_REFERRAL_ATTR,
    type Tracker,
} from '@archipro-website/tracker';
import { useAppData } from '~/modules/root';
import { useIsGoodBot } from '~/modules/root/hook/use-is-good-bot';
import { decodeEntities } from '~/utils';
import { goInCurrentTab, goInNewTab } from '~/utils/urlHelper';

export interface WebsiteTrackingProps {
    websiteURL?: string | null;
    libraryItemID?: number;
    professionalID?: number;
    professionalTitle?: string | null;
}

// If we start linking to client sites, we need to update the tracking to not use this function
// since it doesn't support tracking on right or middle mouse button clicks
export const onAdClickVisitWebsite = (
    trackingProps: WebsiteTrackingProps,
    tracker: Tracker,
    event?: React.MouseEvent,
): void => {
    const {
        websiteURL,
        libraryItemID = 0,
        professionalID = 0,
        professionalTitle = '',
    } = trackingProps;

    const website = getWebsiteLink(websiteURL);

    if (!website) {
        return;
    }

    const customData = {
        LibraryItemID: libraryItemID,
        ProfessionalID: professionalID,
        ProfessionalTitle: professionalTitle,
        ExternalURL: decodeEntities(website),
        ExtraData: {
            MouseEvent: event?.button ?? 0,
        },
    };

    tracker.log('Website', {
        data: customData,
        ga4Data: customData,
        label: professionalTitle,
        url: new URL(window.location.href),
    });

    // `TargetedWebsite` event not tracked here because functions that call this
    // are either internal links or ads. This function should eventually be removed.

    if (
        !event?.currentTarget ||
        event.currentTarget.getAttribute('target') === '_blank'
    ) {
        goInNewTab(website);
    } else {
        goInCurrentTab(website);
    }
};

// Can be removed if/once the above function is removed
const getWebsiteLink = (website?: string | null): string | null => {
    if (!website) {
        return null;
    }

    try {
        const websiteURL = decodeEntities(website);

        return enhanceProfessionalWebsiteLink(
            websiteURL,
            shouldAttachUTM(websiteURL),
        );
    } catch {
        console.error('Invalid URL');
        return null;
    }
};

/**
 * Hook to provide props for external links to enable tracking events. Links
 * should add the returned params as attributes to enable tracking.
 * @param websiteURL The website URL to track.
 * @param inputParams Tracking metadata.
 * @see packages/tracker/src/react-bindings/hooks/useLogLinkClicks.ts
 */
export const useWebsiteReferralTracking = (
    websiteURL: string | null | undefined,
    inputParams: {
        libraryItemID: number;
        professionalID: number;
        professionalTitle: string;
        /** Which CTA surface renders the link; lands as Source on the Website event. */
        source?: string;
    },
) => {
    const selfIdentifiesAsBot = useIsGoodBot();
    const appData = useAppData();

    if (!websiteURL) {
        return {
            referralTrackingURL: undefined,
            referralTrackingParams: {},
        } as const;
    }

    const userTrackingId = appData.user?.APID ?? null;
    const attachUserTrackingId =
        !selfIdentifiesAsBot &&
        userTrackingId &&
        shouldTrackUrl(new URL(websiteURL));

    const trackedWebsite = enhanceProfessionalWebsiteLink(
        websiteURL,
        shouldAttachUTM(websiteURL),
        attachUserTrackingId ? userTrackingId : null,
    );

    return {
        referralTrackingURL: trackedWebsite,
        referralTrackingParams: {
            [TRACK_REFERRAL_ATTR]: '1',
            [REFERRAL_ATTR_ITEM_ID]: inputParams.libraryItemID,
            [REFERRAL_ATTR_PROFESSIONAL_ID]: inputParams.professionalID,
            [REFERRAL_ATTR_PROFESSIONAL_TITLE]: inputParams.professionalTitle,
            ...(inputParams.source && {
                [REFERRAL_ATTR_SOURCE]: inputParams.source,
            }),
            rel: 'noopener',
        },
    } as const;
};

/**
 * this function should be used everywhere to add needed
 * data when linking to a professional website.
 */
export const enhanceProfessionalWebsiteLink = (
    link: string,
    attachUTM: boolean,
    userTrackingId: string | null = null,
): string => {
    try {
        let url = new URL(link);
        if (attachUTM) url = addUtmData(url);
        if (userTrackingId) url = addApIdTracking(url, userTrackingId);
        return url.href;
    } catch {
        return link;
    }
};

const addApIdTracking = (url: URL, userTrackingId: string): URL => {
    if (shouldTrackUrl(url)) {
        url.searchParams.append('__apid', userTrackingId);
    }

    return url;
};

type UTMParam = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_content';

export const addUtmData = (
    url: URL,
    utmData: Record<UTMParam, string> = {
        utm_source: 'archipro',
        utm_medium: 'website',
        utm_campaign: 'website',
        utm_content: '',
    },
): URL => {
    Object.keys(utmData).forEach((key) => {
        const value = utmData[key as UTMParam];
        if (!url.searchParams.has(key) && value) {
            url.searchParams.append(key, value);
        }
    });
    return url;
};

/***
 * super hacky hotfix to stop __apid to be added for provista
 * since it breaks the destination website
 * @param url
 */
const shouldTrackUrl = (url: URL): boolean => {
    const blacklist = [
        'clivebarrington.com',
        'fgrarchitects.com.au',
        'saniflo.co.nz',
        'dormakaba.com',
    ];

    const hostname = url.host.replace(/^www\./, '');

    return !blacklist.includes(hostname);
};

/**
 * super hax for blacklisting clients sites that
 * cannot handle query paramaters at all.
 */
const shouldAttachUTM = (url: string): boolean => {
    const blacklist = ['clivebarrington.com'];
    return !blacklist.includes(new URL(url).host);
};
